import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  CircularProgress,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik, FieldArray, FormikProvider } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { COMPACT_FORM_MODAL_SX, STANDARD_FORM_HEADING_SX } from "../../utils/formModalStyles";
import { getByPatientIdAndLabId, updateResults } from "../../services/PatientlabTestService";

const validationSchema = Yup.object({
  entries: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().required(),
      testResult: Yup.string().required("Result is required"),
      testDate: Yup.string().required("Date is required"),
    })
  ),
});

const EditLabTestForm = ({ open, onClose, labId, patientId }) => {
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTests();
    }
  }, [open]);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const response = await getByPatientIdAndLabId(patientId, labId);
      const transformed = response.map((test) => ({
        id: test.id,
        testName: test.testName,
        assignedByName: test.assignedByName,
        testResult: test.testResult || "",
        testStatus: test.testStatus, 
        testDate: dayjs(new Date(...test.testDate)).format("YYYY-MM-DD"),
      }));
      setInitialData(transformed);
    } catch (error) {
      console.error("Failed to fetch lab test data", error);
      setInitialData([]);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { entries: initialData },
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        // Prepare payload to send to backend
        const payload = values.entries.map((entry) => ({
          id: entry.id,
          testResult: entry.testResult,
          testStatus: "COMPLETED", // or dynamic if needed
          testDate: dayjs(entry.testDate).format("YYYY-MM-DDTHH:mm:ss")
        }));

        // Send update request
        await updateResults(payload);

        try {
          const { recordSampleResultEntered } = await import("../../services/sampleTransactionService");
          for (const entry of values.entries) {
            await recordSampleResultEntered({
              prescriptionId: entry.prescriptionId ?? entry.ipdPrescriptionId,
              patientId: entry.patientId,
              labTestId: entry.testId || entry.labTestId,
              admissionId: entry.admissionId,
              reportId: entry.reportId ?? entry.id,
              patientType: "ipd",
              sampleCollected: true,
              result: true,
            });
          }
        } catch (sampleError) {
          console.warn("[editPatientLabTests] Sample transaction result not saved:", sampleError);
        }

        alert("Lab Test Results Updated Successfully");
        onClose();
      } catch (error) {
        console.error("Update failed:", error);
        alert("Failed to update lab test results.");
      } finally {
        setSubmitting(false);
      }
    },
  });


  const { values, handleChange, handleSubmit, touched, errors } = formik;

  // return (
  //   <BasicModal open={open} onClose={onClose} sx={{ width: 650 }}>
  //     <Box>
  //       <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
  //         Update Lab Test Results
  //       </Typography>

  //       {loading ? (
  //         <Box textAlign="center" py={5}>
  //           <CircularProgress />
  //         </Box>
  //       ) : (
  //         <FormikProvider value={formik}>
  //           <form onSubmit={handleSubmit}>
  //             <FieldArray name="entries">
  //               {() => (
  //                 <Grid container spacing={2}>
  //                   {values.entries.map((item, index) => (
  //                     <React.Fragment key={item.id}>
  //                       <Grid item xs={5}>
  //                         <TextField
  //                           fullWidth
  //                           label="Test Name"
  //                           value={item.testName}
  //                           InputProps={{ readOnly: true }}
  //                         />
  //                       </Grid>

  //                       <Grid item xs={4}>
  //                         <TextField
  //                           fullWidth
  //                           label="Result"
  //                           name={`entries[${index}].testResult`}
  //                           value={item.testResult}
  //                           onChange={handleChange}
  //                           error={
  //                             touched.entries?.[index]?.testResult &&
  //                             Boolean(errors.entries?.[index]?.testResult)
  //                           }
  //                           helperText={
  //                             touched.entries?.[index]?.testResult &&
  //                             errors.entries?.[index]?.testResult
  //                           }
  //                         />
  //                       </Grid>

  //                       <Grid item xs={3}>
  //                         <TextField
  //                           type="date"
  //                           fullWidth
  //                           label="Date"
  //                           name={`entries[${index}].testDate`}
  //                           value={item.testDate}
  //                           onChange={handleChange}
  //                           InputLabelProps={{ shrink: true }}
  //                           error={
  //                             touched.entries?.[index]?.testDate &&
  //                             Boolean(errors.entries?.[index]?.testDate)
  //                           }
  //                           helperText={
  //                             touched.entries?.[index]?.testDate &&
  //                             errors.entries?.[index]?.testDate
  //                           }
  //                         />
  //                       </Grid>
  //                     </React.Fragment>
  //                   ))}

  //                   <Grid item xs={12}>
  //                     <Button type="submit" variant="contained" fullWidth>
  //                       Submit
  //                     </Button>
  //                   </Grid>
  //                 </Grid>
  //               )}
  //             </FieldArray>
  //           </form>
  //         </FormikProvider>
  //       )}
  //     </Box>
  //   </BasicModal>
  // );
  return (
  <BasicModal open={open} onClose={onClose} sx={COMPACT_FORM_MODAL_SX}>
    <Box>
      <Typography sx={STANDARD_FORM_HEADING_SX}>
        Update Lab Test Results
      </Typography>

      {loading ? (
        <Box textAlign="center" py={5}>
          <CircularProgress />
        </Box>
      ) : (
        <FormikProvider value={formik}>
          <form onSubmit={handleSubmit}>
            <FieldArray name="entries">
              {() => (
                <Grid container spacing={2}>
                  {values.entries.map((item, index) => {
                    const isCompleted = item.testStatus === "COMPLETED";

                    return (
                      <React.Fragment key={item.id}>
                        <Grid item xs={3}>
                          <TextField
                            fullWidth
                            label="Test Name"
                            value={item.testName}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <TextField
                            fullWidth
                            label="Assigned By"
                            value={item.assignedByName}
                            InputProps={{ readOnly: true }} 
                          />
                        </Grid>
                        
                        <Grid item xs={3}>
                          <TextField
                            fullWidth
                            label="Result"
                            name={`entries[${index}].testResult`}
                            value={item.testResult}
                            onChange={handleChange}
                            InputProps={{ readOnly: isCompleted }}
                            error={
                              touched.entries?.[index]?.testResult &&
                              Boolean(errors.entries?.[index]?.testResult)
                            }
                            helperText={
                              touched.entries?.[index]?.testResult &&
                              errors.entries?.[index]?.testResult
                            }
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            type="date"
                            fullWidth
                            label="Date"
                            name={`entries[${index}].testDate`}
                            value={item.testDate}
                            onChange={handleChange}
                            InputProps={{ readOnly: isCompleted }}
                            InputLabelProps={{ shrink: true }}
                            error={
                              touched.entries?.[index]?.testDate &&
                              Boolean(errors.entries?.[index]?.testDate)
                            }
                            helperText={
                              touched.entries?.[index]?.testDate &&
                              errors.entries?.[index]?.testDate
                            }
                          />
                        </Grid>
                      </React.Fragment>
                    );
                  })}

                  <Grid item xs={12}>
                    <FormModalActions
                      onCancel={onClose}
                      submitType="submit"
                      loading={submitting}
                    />
                  </Grid>
                </Grid>
              )}
            </FieldArray>
          </form>
        </FormikProvider>
      )}
    </Box>
  </BasicModal>
);

};

export default EditLabTestForm;